import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../utils/apiClient';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useNotifications } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '../utils/errorMessage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MoveDevicesDialog = ({ 
  open, 
  onClose, 
  selectedDeviceIds = [], 
  onDataUpdate 
}) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const { t } = useTranslation('admin');
  const [targetLocationId, setTargetLocationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Загрузка локаций из API
  useEffect(() => {
    if (open) {
      loadLocations();
    }
  }, [open]);

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await api.get(`${API_BASE_URL}locations`);

      // Обрабатываем ответ - данные находятся в response.data.locations
      const locationsData = response.data?.locations || response.data?.results || response.data || [];

      // API уже предоставляет full_path, используем его
      const locationsList = locationsData.map(loc => ({
        id: loc.id,
        name: loc.name,
        path: loc.full_path || loc.name // Используем full_path из API, если есть
      }));

      setLocations(locationsList);
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setLoadingLocations(false);
    }
  };

  // API: Изменение локации устройств
  const apiChangeDeviceLocation = async (deviceIds, locationId) => {
    try {
      setIsLoading(true);

      // Если locationId пустая строка, преобразуем в null (убрать локацию)
      const actualLocationId = locationId === '' || locationId === null || locationId === undefined
        ? null
        : locationId;

      const payload = {
        device_ids: deviceIds,
        location_id: actualLocationId
      };

      await api.patch(`${API_BASE_URL}devices/change-location`, payload);

      showSuccess(actualLocationId === null
        ? t('moveLocationRemoved', { count: deviceIds.length })
        : t('moveLocationChanged', { count: deviceIds.length }));

      // Обновляем данные
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      showError(getErrorMessage(error));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedDeviceIds.length === 0) {
      showWarning(t('moveNoDevices'));
      return;
    }

    try {
      // Передаем пустую строку или null - оба значения преобразуются в null в API функции
      await apiChangeDeviceLocation(selectedDeviceIds, targetLocationId || null);
      handleClose();
    } catch (error) {
      // Ошибка уже обработана в apiChangeDeviceLocation
    }
  };

  const handleClose = () => {
    setTargetLocationId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('moveTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('moveSelectedCount', { count: selectedDeviceIds.length })}
        </Typography>

        <FormControl fullWidth margin="dense">
          <InputLabel>{t('moveTargetLocation')}</InputLabel>
          <Select
            value={targetLocationId}
            onChange={(e) => setTargetLocationId(e.target.value)}
            label={t('moveTargetLocation')}
            disabled={loadingLocations || isLoading}
          >
            <MenuItem value="">
              <em>{t('moveNoLocation')}</em>
            </MenuItem>
            {loadingLocations ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('moveLoadingLocations')}
              </MenuItem>
            ) : (
              locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.path}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        
        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
          {targetLocationId === ''
            ? t('moveHintRemove')
            : t('moveHintChange')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || loadingLocations}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('moveMoving') : t('moveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MoveDevicesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDeviceIds: PropTypes.array,
  onDataUpdate: PropTypes.func,
};

export default MoveDevicesDialog;

